import React, { useEffect, useState } from 'react';
import '../../../themes/global.scss';
import '../../../themes/home.scss';
import { useDispatch, useSelector } from 'react-redux';
import ReactSwitch from 'react-switch';
import { motion } from 'framer-motion';
import moment from 'moment';

import MobileAppModal from '../../../commonComponents/MobileAppModal';
import {
  createLoadingSelector,
} from '../../../api/selectors';
import CustomSpinner from '../../../commonComponents/CustomSpinner';
import {
  FEEDBACK_REQUIRED,
  getAllProfessionals,
  getAllSkillCategories,
} from '../../../actions';
import { Colors } from '../../../themes/colors';
import history from '../../../history';

import houseIcon from '../../../images/house-icon.svg';
import calendar from '../../../images/calendar-icon.svg';
import arrow from '../../../images/arrow_right.svg';
import startClock from '../../../images/start-clock.svg';
import endClock from '../../../images/end-clock.svg';
import userIcon from '../../../images/user-icon.svg';
import breakIcon from '../../../images/break-icon.svg';
import rateIcon from '../../../images/rate-icon.svg';
import autoFillIcon from '../../../images/autofill-icon.svg';
import briefcaseIcon from '../../../images/briefcase.svg';

import ModalRatePopup from '../ModalRatePopup';
import ShowStartTime from '../../../commonComponents/showStartTime';
import SkillsPopup from './SkillsPopup';
import isHoliday from '../isHoliday';
import WarningPopup from '../WarningPopup';
import BackButton from '../../../commonComponents/BackButton';
import ProfessionPopup from './ProfessionPopup';
import CalendarPopup from '../../../commonComponents/CalendarPopup';
import Tooltip from '../../Tooltip';
import { getAutoFillInfo } from '../../../constants';
import { workerClassificationTooltipDescription } from '../constants';

export default function JobPost() {
  const dispatch = useDispatch();

  const averageRating = useSelector(
    (state) => state.job.averageRating || { average: 49, max: 100, min: 18 },
  );
  const allProfessions = useSelector((state) => state.user.allProfessions);

  const parentUser = useSelector((state) => state.user);
  const user = useSelector(
    (state) => state.user.selectedChildOffice,
  );

  const loadingSelector = createLoadingSelector([FEEDBACK_REQUIRED]);
  const isLoading = useSelector((state) => loadingSelector(state));

  const [professionType, setProfessionType] = useState('Dental Hygienist');
  const [skills, setSkills] = useState('skill-general');
  const [enableAutoFill, setEnableAutoFill] = useState(false);
  const [offerStartDate, setOfferStartDate] = useState(history.location?.state?.date ?? null);
  const [offerStartTime, setOfferStartTime] = useState(null);
  const [offerEndTime, setOfferEndTime] = useState(null);
  const [offerRate, setOfferRate] = useState(0);
  const [lunch_break_time, setLunchBreakTime] = useState('');
  const [is_lunch_break_paid, setIsLunchBreakPaid] = useState(false);
  const [content, setContent] = useState(null);
  const [resultFromModal, setResultFromModal] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  // popups
  const [showRatePopup, setShowRatePopup] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);

  const [showStartTime, setShowStartTime] = useState(false);
  const [confirmPostOnHoliday, setConfirmPostOnHoliday] = useState(false);

  const allSkillCategories = useSelector((state) => state.user.allSkillCategories);

  const defaultSkills = () => {
    if (skills) {
      return [skills];
    } if (professionType === 'DA') {
      return ['skill-da-general'];
    } if (professionType === 'DN') {
      return ['experienced-procedures-oral-examinations'];
    }
    return ['skill-general'];
  };

  useEffect(() => {
    if (!allProfessions || allProfessions?.length === 0) {
      dispatch(getAllProfessionals());
    }
    if (!allSkillCategories?.length) {
      dispatch(getAllSkillCategories());
    }
  }, []);

  const sendOffer = () => {
    const localDate = moment(offerStartDate).format('yyyy-MM-DD');
    const selectedProfession = allProfessions.filter(
      (el) => el.name === professionType,
    );

    const skillsObj = skills ? { skills: [skills] } : { skills: defaultSkills() };
    const jobData = {
      localDate,
      localStart: moment(offerStartTime).format('hh:mm a'),
      localEnd: moment(offerEndTime).format('hh:mm a'),
      rate: parseFloat(offerRate),
      break_time: parseInt(lunch_break_time),
      is_break_paid: is_lunch_break_paid,
      profession: selectedProfession[0]?.code || null,
      prefer_type: 'firs_available',
      ...skillsObj,
      autoFill: enableAutoFill,
    };
    history.push('/dashboard/guideline', { jobData });
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const date = (event) => {
    setOfferStartDate(event);
  };

  const startTime = (event) => {
    setOfferStartTime(moment(event, 'hh:mm aa'));
  };

  const endTime = (event) => {
    setOfferEndTime(moment(event, 'hh:mm aa'));
  };

  const breakTime = (event) => {
    setLunchBreakTime(event.time);
    setIsLunchBreakPaid(event.paid);
  };

  const showContent = (type) => {
    if (type === 'start_time') {
      setContent(type);

      setResultFromModal({ result: startTime });
    } else if (type === 'end_time') {
      setContent(type);

      setResultFromModal({ result: endTime });
    } else if (type === 'break') {
      setContent(type);

      setResultFromModal({ result: breakTime });
    }

    openModal();
  };

  const sendOfferConfirmation = () => {
    if (isHoliday(offerStartDate)) {
      setConfirmPostOnHoliday(true);
    } else {
      sendOffer();
    }
  };

  const handleProfessionChange = (updatedProfession) => {
    let selectedSkill = 'skill-general';
    if (updatedProfession === 'Dental Assistant') {
      selectedSkill = 'skill-da-general';
    } else if (updatedProfession === 'Associate Dentist') {
      selectedSkill = 'experienced-procedures-oral-examinations';
    }

    setProfessionType(updatedProfession);
    setSkills(selectedSkill);
  };

  const showTime = (type) => {
    if (type === 'start_time') {
      setContent(type);
      setResultFromModal({ result: startTime });
    } else if (type === 'end_time') {
      setContent(type);
      setResultFromModal({ result: endTime });
    }
    setShowStartTime(true);
  };

  const renderTimeField = (title, value, contentType, icon) => (
    <div
      className="row_container"
      style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}
      onClick={() => showTime(contentType)}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            alt="icon"
            src={icon}
            style={{ width: 24, height: 24, marginRight: 8 }}
          />
          <p className="date">{title}</p>
        </div>
        <p
          style={{
            fontFamily: 'Nunito',
            color: Colors.neutral_600,
            textTransform: 'capitalize',
          }}
        >
          {value}
        </p>
      </div>
      <div>
        <img
          alt="arrow"
          src={arrow}
          style={{ marginLeft: 30, marginRight: -30 }}
        />
      </div>
    </div>
  );

  const renderJobField = (title, value, contentType, icon) => (
    <div
      className="row_container"
      style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}
      onClick={() => showContent(contentType)}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            alt="icon"
            src={icon}
            style={{ width: 24, height: 24, marginRight: 8 }}
          />
          <p className="date">{title}</p>
        </div>
        <p
          style={{
            fontFamily: 'Nunito',
            color: Colors.neutral_400,
            textTransform: 'capitalize',
          }}
        >
          {value}
        </p>
      </div>
      <div>
        <img
          alt="arrow"
          src={arrow}
          style={{ marginLeft: 30, marginRight: -30 }}
        />
      </div>
    </div>
  );

  const renderCalendarField = (title, value) => (
    <div
      className="row_container"
      style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}
      onClick={() => setShowCalendarPopup(true)}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            alt="icon"
            src={calendar}
            style={{ width: 24, height: 24, marginRight: 8 }}
          />
          <p className="date">{title}</p>
        </div>
        <p
          style={{
            fontFamily: 'Nunito',
            color: Colors.neutral_600,
            textTransform: 'capitalize',
          }}
        >
          {value}
        </p>
      </div>
      <div>
        <img
          alt="arrow"
          src={arrow}
          style={{ marginLeft: 30, marginRight: -30 }}
        />
      </div>
    </div>
  );

  const selectedProfession = allProfessions
    .filter((el) => el.name === professionType)
    .map((el) => el.code)[0];

  const profRate = averageRating[selectedProfession];

  const autoFillTooltipDescription = (
    <>
      Professional’s eligibility criteria:
      <br />
      <br />
      {'\u2022  '}
      Has a minimum of 1 year working experience.
      <br />
      {'\u2022  '}
      Has received exceptional performance ratings on over 95% of completed shifts.
      <br />
      {'\u2022  '}
      Has completed at least 3 shifts.
      <br />
      <br />
      Note: You may change your auto-confirm preference at any time by visiting the Shift Details page.
    </>
  );

  let btnDisabled;

  if (
    !offerStartDate
    || !offerStartTime
    || !offerEndTime
    || offerRate === 0
    || lunch_break_time.length === 0
    // || !professionType
  ) {
    btnDisabled = true;
  } else {
    btnDisabled = false;
  }

  if (isLoading) {
    return <CustomSpinner isLoading={isLoading} />;
  }

  let modalDefault = '';

  if (content === 'start_time') {
    modalDefault = offerStartTime;
  } else if (content === 'end_time') {
    modalDefault = offerEndTime;
  }

  const opacityVariants = {
    show: { opacity: 1, display: 'flex' },
    hide: { opacity: 0, transitionEnd: { display: 'none' } },
  };

  return (
    <div className="component_container">
      <div className="floating_container">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div style={{ width: '95%', marginTop: 30 }}>
            <BackButton />
          </div>

          <h1
            className="global_font"
            style={{
              color: Colors.neutral_600,
              fontSize: 38,
              fontWeight: 700,
              width: '100%',
              textAlign: 'center',
              margin: 0,
              marginBottom: 10,
            }}
          >
            Single-Day Temp
          </h1>

          <div style={{ width: '50%' }}>
            <div
              className="row_container"
              style={{ cursor: 'default', borderBottom: `1px solid ${Colors.neutral_100}` }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    alt="icon"
                    src={houseIcon}
                    style={{ width: 24, height: 24, marginRight: 8 }}
                  />

                  <p
                    className="date"
                    style={{
                      marginRight: 16,
                    }}
                  >
                    Office:
                  </p>
                </div>

                <p
                  style={{
                    fontFamily: 'Nunito',
                    color: Colors.neutral_600,
                  }}
                >
                  {user?.office_name}
                </p>
              </div>
            </div>

            {parentUser.childOffices.length > 0
              && user.isWorkerClassificationEnabled
              && user.workerClassification
              && (
                <div
                  className="row_container"
                  style={{ cursor: 'default', borderBottom: `1px solid ${Colors.neutral_100}` }}
                >
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        alt="icon"
                        src={briefcaseIcon}
                        style={{ width: 24, height: 24, marginRight: 8 }}
                      />

                      <p
                        className="date"
                        style={{
                          marginRight: 16,
                        }}
                      >
                        Worker Type:

                        <span
                          style={{
                            marginLeft: '5px',
                            position: 'relative',
                            top: '5px',
                          }}
                        >
                          <Tooltip
                            content={workerClassificationTooltipDescription}
                          />
                        </span>
                      </p>
                    </div>

                    <p
                      style={{
                        fontFamily: 'Nunito',
                        color: Colors.neutral_600,
                      }}
                    >
                      {user.workerClassification.classificationType === 'contractor'
                        ? 'Contracted'
                        : 'Employed'}
                    </p>
                  </div>
                </div>
              )}

            {renderCalendarField('Date:',
              !offerStartDate
                ? <span style={{ color: Colors.neutral_300 }}>Date</span>
                : moment(offerStartDate).format('ddd, MMM DD, YYYY'))}

            {allProfessions?.length > 1
              && (
                <ProfessionPopup
                  title="Professional:"
                  value={!professionType ? <span style={{ color: Colors.neutral_300 }}>Profession Type</span> : professionType}
                  icon={userIcon}
                  type="profession"
                  submitProfession={handleProfessionChange}
                />
              )}
            <SkillsPopup
              skillsSubmit={(selectedSkills) => {
                setSkills(selectedSkills);
              }}
              title={professionType === 'Associate Dentist' ? 'Shift Procedures:' : 'Preferred Specialty:'}
              professionType={professionType}
              defaultSkills={skills}
              tooltip={professionType === 'Associate Dentist' ? 'Here you can list all the procedures that the Professional will be expected to complete. This makes it easier for both the temp and offices so both parties are on the same page in regards to what the Dentist will be specifically doing on the shift.' : null}
              isAssociateDentist={professionType === 'Associate Dentist'}
            />

            {renderTimeField(
              'Arrival:',
              !offerStartTime
                ? <span style={{ color: Colors.neutral_300 }}>Time</span>
                : moment(offerStartTime).format('hh:mm a'),
              'start_time',
              startClock,
            )}

            {renderTimeField(
              'Departure:',
              !offerEndTime ? <span style={{ color: Colors.neutral_300 }}>Time</span> : moment(offerEndTime).format('hh:mm a'),
              'end_time',
              endClock,
            )}

            <div
              className="row_container"
              style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}
              onClick={() => setShowRatePopup(true)}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    alt="icon"
                    src={rateIcon}
                    style={{ width: 24, height: 24, marginRight: 8 }}
                  />

                  <p
                    className="date"
                    style={{
                      marginRight: 16,
                    }}
                  >
                    Hourly Rate:
                  </p>
                </div>

                <p
                  style={{
                    fontFamily: 'Nunito',
                    color: Colors.neutral_600,
                  }}
                >
                  {offerRate ? `$ ${offerRate}` : <span style={{ color: Colors.neutral_300 }}>$ ⏤.⏤</span>}
                </p>
              </div>
              <div>
                <img
                  alt="arrow"
                  src={arrow}
                  style={{ marginLeft: 30, marginRight: -30 }}
                />
              </div>
            </div>

            <div
              className="row_container"
              style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}
              onClick={() => showContent('break')}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    alt="icon"
                    src={breakIcon}
                    style={{ width: 24, height: 24, marginRight: 8 }}
                  />

                  <p className="date" style={{ marginRight: 16 }}>
                    Break:
                  </p>
                </div>

                <p
                  style={{
                    fontFamily: 'Nunito',
                    color: Colors.neutral_600,
                  }}
                >
                  {lunch_break_time
                    ? `${lunch_break_time} min ${is_lunch_break_paid ? '(Paid)' : '(Unpaid)'
                    }`
                    : <span style={{ color: Colors.neutral_300 }}>Time</span>}
                </p>
              </div>
              <div>
                <img
                  alt="arrow"
                  src={arrow}
                  style={{ marginLeft: 30, marginRight: -30 }}
                />
              </div>
            </div>

            <div style={{ borderBottom: `1px solid ${Colors.neutral_100}` }}>
              <div
                className="row_container"
                style={{ cursor: 'default' }}
              >
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      alt="icon"
                      src={autoFillIcon}
                      style={{
                        width: 24,
                        height: 24,
                        marginLeft: 4,
                        marginRight: 6,
                      }}
                    />

                    <p
                      className="date"
                      style={{
                        marginRight: 16,
                      }}
                    >
                      Auto-Confirm:

                      <span
                        style={{
                          marginLeft: '5px',
                          position: 'relative',
                          top: '5px',
                        }}
                      >
                        <Tooltip
                          content={autoFillTooltipDescription}
                        />
                      </span>
                    </p>
                  </div>
                </div>

                <motion.div
                  animate={getAutoFillInfo(professionType, skills).show ? 'show' : 'hide'}
                  variants={opacityVariants}
                >
                  <ReactSwitch
                    checked={enableAutoFill}
                    onChange={() => setEnableAutoFill(!enableAutoFill)}
                    onColor={Colors.secondary_500}
                    onHandleColor="#fff"
                    handleDiameter={20}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    offColor={Colors.neutral_100}
                    boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
                    activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                    height={25}
                    width={50}
                    className="react-switch"
                    id="material-switch"
                  />
                </motion.div>
              </div>

              <motion.p
                key={getAutoFillInfo(professionType, skills).message}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="global_font"
                style={{
                  color: Colors.neutral_500,
                  fontStyle: 'italic',
                  padding: '0px 40px',
                  marginTop: 0,
                  marginBottom: 25,
                }}
              >
                {getAutoFillInfo(professionType, skills).message}
              </motion.p>
            </div>

            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 100,
                marginBottom: 80,
              }}
            >
              <button
                type="button"
                className="green-button"
                disabled={btnDisabled}
                style={{
                  alignSelf: 'center',
                  width: 300,
                  backgroundColor: btnDisabled ? Colors.primary_200 : Colors.primary_500,
                  marginTop: 20,
                }}
                onClick={sendOfferConfirmation}
              >
                <p className="white-text-18">Send Request</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      <MobileAppModal
        show={modalIsOpen}
        closeModal={closeModal}
        height={973}
        defaultValue={modalDefault}
        content={content}
        result={resultFromModal?.result}
      />

      {showStartTime ? (
        <ShowStartTime
          content={content}
          result={resultFromModal?.result}
          closeModal={() => setShowStartTime(false)}
          height={973}
          defaultStartTime={offerStartTime}
          defaultEndTime={offerEndTime}
        />
      ) : null}

      {showRatePopup ? (
        <ModalRatePopup
          closeModal={() => setShowRatePopup(false)}
          submitRate={(rate) => setOfferRate(rate)}
          averagePrice={profRate}
          defaultValue={offerRate}
        />
      ) : (
        <div />
      )}

      {showCalendarPopup ? (
        <CalendarPopup
          closeModal={() => setShowCalendarPopup(false)}
          confirmDateBtn={(date) => setOfferStartDate(date)}
        />
      ) : (
        <div />
      )}

      {confirmPostOnHoliday ? (
        <WarningPopup
          title="Major U.S Holiday Conflict"
          text={
            'Looks like you are trying to post a shift on a\nMajor U.S. Holiday. Please make sure your office\nwill be open on this date!'
          }
          successText="Continue"
          cancelText="Go Back"
          btnSucces={() => {
            sendOffer();
            setConfirmPostOnHoliday(false);
          }}
          btnCancel={() => setConfirmPostOnHoliday(false)}
        />
      ) : (
        <div />
      )}
    </div>
  );
}
